import { useEffect, useState, type FC } from 'react';
import { LuBellRing, LuX } from 'react-icons/lu';
import aviso from '../lib/aviso';
import { ativarAviso, desativarAviso, estadoDoAviso, type EstadoAviso } from '../lib/avisoNoAparelho';
import { useCelular } from '../hooks/useCelular';
import './AvisosNoAparelho.css';

/**
 * A porta para receber lead novo no aparelho, em dois lugares:
 * - `faixa`: no topo do sino, no computador e no celular. É onde o aviso mora, e onde se desliga.
 * - `banner`: só no celular, abaixo do cabeçalho, até a pessoa ativar ou dispensar. Sem ele, quem
 *   reclamou de não receber aviso não acharia a porta dentro do sino.
 */

const DISPENSADO = 'brik.avisoNoAparelho.dispensado';

const emModoSuporte = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.emModoSuporte === true; }
    catch { return false; }
};

const useEstadoAviso = () => {
    const [estado, setEstado] = useState<EstadoAviso | null>(null);
    const [ocupado, setOcupado] = useState(false);

    useEffect(() => {
        let vivo = true;
        const medir = () => {
            estadoDoAviso().then(e => { if (vivo) setEstado(e); }).catch(() => { if (vivo) setEstado('indisponivel'); });
        };
        medir();
        /* Quem libera a permissão faz isso fora do app (configurações do site ou do Android).
           Ao voltar, a faixa se refaz sozinha, sem precisar recarregar. */
        const aoVoltar = () => { if (document.visibilityState === 'visible') medir(); };
        document.addEventListener('visibilitychange', aoVoltar);
        return () => { vivo = false; document.removeEventListener('visibilitychange', aoVoltar); };
    }, []);

    const executar = async (acao: () => Promise<EstadoAviso>) => {
        setOcupado(true);
        try {
            const novo = await acao();
            setEstado(novo);
            return novo;
        } catch {
            aviso.erro('Não foi possível mudar os avisos neste aparelho.');
            return null;
        } finally {
            setOcupado(false);
        }
    };

    const ativar = async () => {
        const novo = await executar(ativarAviso);
        if (novo === 'ativo') aviso.ok('Pronto: você recebe aqui cada lead novo.');
        else if (novo === 'bloqueado') aviso.erro('O navegador bloqueou os avisos. Libere nas configurações do site.');
    };

    const desativar = async () => {
        const novo = await executar(desativarAviso);
        if (novo === 'desligado') aviso.ok('Avisos desligados neste aparelho.');
    };

    return { estado, ocupado, ativar, desativar };
};

const PassoIphone = () => (
    <span className="ana-texto">
        Para receber lead novo no iPhone, adicione o Brik à Tela de Início: toque em <strong>Compartilhar</strong> e
        depois em <strong>Adicionar à Tela de Início</strong>. Abra o Brik pelo ícone e ative aqui.
    </span>
);

/* O navegador já respondeu "não" e não pergunta de novo: só a pessoa desfaz, fora do app. */
const PassoDesbloquear = () => (
    <span className="ana-texto">
        Os avisos estão bloqueados neste navegador. Toque no <strong>cadeado</strong> ao lado do endereço,
        em <strong>Permissões</strong> e libere <strong>Notificações</strong>. No Android, confira também se as
        notificações do navegador estão ligadas nas configurações do celular.
    </span>
);

export const AvisosNoAparelhoFaixa: FC = () => {
    const { estado, ocupado, ativar, desativar } = useEstadoAviso();
    if (!estado || estado === 'indisponivel' || emModoSuporte()) return null;

    return (
        <div className={`ana-faixa ${estado === 'ativo' ? 'is-ativo' : ''}`}>
            <LuBellRing className="ana-icone" />
            {estado === 'ativo' && <>
                <span className="ana-texto">Avisos de lead novo ativos neste aparelho.</span>
                <button type="button" className="ana-link" onClick={desativar} disabled={ocupado}>Desligar</button>
            </>}
            {estado === 'desligado' && <>
                <span className="ana-texto">Receba neste aparelho um aviso a cada lead novo.</span>
                <button type="button" className="ana-btn" onClick={ativar} disabled={ocupado}>
                    {ocupado ? 'Ativando…' : 'Ativar'}
                </button>
            </>}
            {estado === 'bloqueado' && <PassoDesbloquear />}
            {estado === 'instalar-iphone' && <PassoIphone />}
        </div>
    );
};

export const AvisosNoAparelhoBanner: FC = () => {
    const celular = useCelular();
    const { estado, ocupado, ativar } = useEstadoAviso();
    const [dispensado, setDispensado] = useState(() => localStorage.getItem(DISPENSADO) === '1');
    /* O passo a passo (bloqueado, iPhone) tem quatro ou cinco linhas: aberto de
       saída, empurrava a tela ~110px para baixo. Começa numa linha só, e o
       caminho aparece quando a pessoa pede. */
    const [comoFazer, setComoFazer] = useState(false);

    if (!celular || dispensado || emModoSuporte()) return null;
    if (estado !== 'desligado' && estado !== 'instalar-iphone' && estado !== 'bloqueado') return null;

    const dispensar = () => {
        localStorage.setItem(DISPENSADO, '1');
        setDispensado(true);
    };

    const temPasso = estado === 'instalar-iphone' || estado === 'bloqueado';

    return (
        <div className={`ana-banner ${comoFazer ? 'is-aberto' : ''}`} role="region" aria-label="Avisos no celular">
            <LuBellRing className="ana-icone" />
            {estado === 'desligado' && <span className="ana-texto">Receba no celular um aviso a cada lead novo.</span>}
            {estado === 'instalar-iphone' && <span className="ana-texto">Receba no iPhone um aviso a cada lead novo.</span>}
            {estado === 'bloqueado' && <span className="ana-texto">Os avisos de lead novo estão bloqueados aqui.</span>}
            {estado === 'desligado' && (
                <button type="button" className="ana-btn" onClick={ativar} disabled={ocupado}>
                    {ocupado ? 'Ativando…' : 'Ativar avisos'}
                </button>
            )}
            {temPasso && (
                <button type="button" className="ana-link" aria-expanded={comoFazer} onClick={() => setComoFazer(v => !v)}>
                    {comoFazer ? 'Esconder' : estado === 'bloqueado' ? 'Como liberar' : 'Como ativar'}
                </button>
            )}
            <button type="button" className="ana-fechar" onClick={dispensar} aria-label="Dispensar">
                <LuX />
            </button>
            {comoFazer && (
                <div className="ana-passo">
                    {estado === 'instalar-iphone' ? <PassoIphone /> : <PassoDesbloquear />}
                </div>
            )}
        </div>
    );
};
